import * as React from "react"
import { Link, graphql } from "gatsby"

import Layout from "../components/layout"
import Seo from "../components/seo"
import HowItWorksVideo from "../videos/based-cdn-demo.mp4"
import { codesnippet } from "@basedcdn/basedcdn-demo"
import assetSheet from "../images/asset-sheet.svg"

const Homepage = ({ data, location }) => {
  const siteTitle = data.site.siteMetadata?.title || `Title`

  return (
    <Layout location={location} title={siteTitle}>
      <Seo
        title="BasedCDN | Design to Development Asset Pipeline for Figma"
        description="Upload your assets to a CDN and share them with your development teams
"
      />
      {renderSeeItVideo()}
      {viewAssetSheet()}
      {renderTryYourself()}
      {renderHowItWorks()}
      {renderFaq()}
      {renderBlogSection(data)}
    </Layout>
  )
}

const renderSeeItVideo = () => {
  return (
    <div>
      <h2 style={{ textAlign: "center", marginBottom: 48 }}>
        Upload your Figma assets to a CDN
      </h2>
      <div>
        <div className="video-wrapper">
          <video autoplay="autoplay" loop muted width="100%" height="auto">
            <source src={HowItWorksVideo} type="video/mp4" />
            Sorry, your browser doesn't support embedded videos.
          </video>
        </div>
      </div>
    </div>
  )
}

const viewAssetSheet = () => {
  return (
    <div style={{ marginTop: 140 }}>
      <h2 style={{ textAlign: "center", marginBottom: 48 }}>
        All of your graphics, one Asset Sheet
      </h2>
      <div>
        <img src={assetSheet} style={{ maxWidth: "100%", display: "block" }} />
      </div>
      <div style={{ textAlign: "center" }}>
        <Link
          to={"https://app.basedcdn.com/collections/basedcdn-demo"}
          itemProp="url"
          target="_blank"
        >
          View our demo Asset Sheet
        </Link>
      </div>
    </div>
  )
}

const renderTryYourself = () => {
  return (
    <div style={{ marginTop: 140 }}>
      <h2 style={{ textAlign: "center", marginBottom: 48 }}>
        Install the assets via NPM
      </h2>
      <code>npm i https://static.basedcdn.com/basedcdn-demo/src/dist.zip</code>
      <div className="image-wrapper">
        <img src={codesnippet} style={{ maxWidth: "100%", display: "block" }} />
      </div>
    </div>
  )
}

const renderHowItWorks = () => {
  return (
    <div>
      <h2>How it Works</h2>
      <p>
        We leverage cloud technology to host and serve your design assets. Each
        asset is uploaded onto our static file system and then cached onto over
        200+ cloud edge locations. That means when an end user comes to your
        website they will have incredible fast asset load time. You no longer
        will have to coordinate asset updates with your development team. You
        can simply use BasedCDN to make updates directly and publish to the
        cloud.
      </p>
    </div>
  )
}

const renderFaq = () => {
  return (
    <div>
      <h2>FAQ</h2>
      <ul>
        <li>
          <label>
            <b>What is BasedCDN?</b>
          </label>
          <p>
            BasedCDN is a design to development asset pipeline. We make it
            possible for your design team to seamlessly make updates to assets
            in production without requiring development resources. We enable
            your team to make required design changes and publish these to a
            CDN. Once published you can share all of your asset URL's with
            development to use as the asset source.
          </p>
        </li>
        <li>
          <label>
            <b>Why do you need my email?</b>
          </label>
          <p>
            We use your email as your internal ID and as a way to communicate
            with you.
          </p>
        </li>
        <li>
          <label>
            <b>What is a namespace?</b>
          </label>
          <p>
            The namespace is used as a unique resource identifier. This
            namespace will be used on all of your URL's in this format:
            <code>
              https://static.basedcdn.com/&#60;namespace&#62;/asset.png
            </code>
          </p>
        </li>
        <li>
          <label>
            <b>How much does this cost?</b>
          </label>
          <p>
            We offer a free plan for up to 100k requests. After 100k requests we
            will bill you $5/mo.
          </p>
        </li>
        <li>
          <label>
            <b>How long does it take to update?</b>
          </label>
          <p>
            Assets will update instantly but in somecases up to 15 seconds.
            After an asset is updated we invalidate all versions of the
            previously cached asset.
          </p>
        </li>
        <li>
          <label>
            <b>What format is the asset in?</b>
          </label>
          <p>Currently we only support PNG formats.</p>
        </li>
        <li>
          <label>
            <b>What is the maximum file size allowed?</b>
          </label>
          <p>Currently the maximum size for file uploads is 5.85mb.</p>
        </li>
      </ul>
    </div>
  )
}

const renderBlogSection = data => {
  const posts = data.allMarkdownRemark.nodes
  return (
    <div>
      <h2>From our Blog</h2>
      <ol style={{ listStyle: `none` }}>
        {posts.map(post => {
          const title = post.frontmatter.title || post.fields.slug

          return (
            <li key={post.fields.slug}>
              <article
                className="post-list-item"
                itemScope
                itemType="http://schema.org/Article"
              >
                <header>
                  <h2>
                    <Link to={post.fields.slug} itemProp="url">
                      <span itemProp="headline">{title}</span>
                    </Link>
                  </h2>
                  <small>{post.frontmatter.date}</small>
                </header>
                <section>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: post.frontmatter.description || post.excerpt,
                    }}
                    itemProp="description"
                  />
                </section>
              </article>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

export default Homepage

export const pageQuery = graphql`
  query {
    site {
      siteMetadata {
        title
      }
    }
    allMarkdownRemark(sort: { fields: [frontmatter___date], order: DESC }) {
      nodes {
        excerpt
        fields {
          slug
        }
        frontmatter {
          date(formatString: "MMMM DD, YYYY")
          title
          description
        }
      }
    }
  }
`
